import React, { useState } from "react"
import { useAccessContext } from "../component/guard/AccessGuard"
import { api, firestore } from "../core"
import { UserId, UserPersonalNotification } from "popopo-msg"
import { useUserPersonalNotifications, markPersonalNotificationAsRead } from "popopo-lib-front/firestore-access"
import { QuillViewer, PersonalNotificationIcon } from "popopo-lib-front/component"
import { Modal, Button } from "react-bootstrap"
import { useToast } from "popopo-lib-front/hook"

export const PersonalNotifications = () => {
  const context = useAccessContext()
  const { user } = context
  const userId = UserId(user.id)
  const toast = useToast()
  const [selectedNotification, setSelectedNotification] = useState<UserPersonalNotification | null>(null)
  const [markingAsRead, setMarkingAsRead] = useState<string | null>(null)
  const [receivingContent, setReceivingContent] = useState<string | null>(null)

  const personalNotifications = useUserPersonalNotifications(firestore, userId, 50)

  const handleMarkAsRead = async (notification: UserPersonalNotification) => {
    if (notification.is_read || markingAsRead === notification.id) return

    setMarkingAsRead(notification.id)
    try {
      await markPersonalNotificationAsRead(firestore, userId, notification.id)
      setSelectedNotification(null)
      toast.add({ type: "success", title: "既読", message: "既読にしました" })
    } catch (error) {
      console.error("既読マークに失敗しました:", error)
      toast.add({ type: "danger", title: "エラー", message: "既読マークに失敗しました" })
    } finally {
      setMarkingAsRead(null)
    }
  }

  const handleReceiveContent = async (notification: UserPersonalNotification) => {
    if (!notification.delivery_content || notification.delivery_content.received_at || receivingContent === notification.id) return

    setReceivingContent(notification.id)
    try {
      const token = await context.credential.user.getIdToken()
      const result = await api.PUT("/api/v2/personal-notifications/{personalNotificationId}/delivery-content", {
        params: {
          path: {
            personalNotificationId: notification.id,
          },
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: {
          status: "received",
        },
      })

      if (result.error) {
        console.error("受け取りに失敗しました:", result.error)
        toast.add({ type: "danger", title: "受取失敗", message: JSON.stringify(result.error) })
        return
      }

      toast.add({ type: "success", title: "受取完了", message: "アイテムを受け取りました" })
      setSelectedNotification(null)
    } catch (error) {
      console.error("受け取りに失敗しました:", error)
      toast.add({ type: "danger", title: "エラー", message: "受け取りに失敗しました" })
    } finally {
      setReceivingContent(null)
    }
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    })
  }

  if (personalNotifications === "loading") {
    return (
      <div className="text-center py-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">個人通知を読み込み中...</p>
      </div>
    )
  }

  if (personalNotifications.length === 0) {
    return (
      <div className="alert alert-info">
        <i className="fas fa-inbox me-2"></i>
        まだ個人通知はありません
      </div>
    )
  }

  return (
    <>
      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              <th>アイコン</th>
              <th>タイトル</th>
              <th>配信日時</th>
              <th>既読状態</th>
              <th>配信コンテンツ</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {personalNotifications.map((notification) => (
              <tr key={notification.id}>
                <td>
                  <PersonalNotificationIcon icon={notification.icon} />
                </td>
                <td>{notification.title}</td>
                <td>{formatDate(notification.scheduled_delivery_at)}</td>
                <td>
                  {notification.is_read ? (
                    <span className="badge bg-secondary">既読</span>
                  ) : (
                    <span className="badge bg-primary">未読</span>
                  )}
                </td>
                <td>
                  {notification.delivery_content ? (
                    notification.delivery_content.received_at ? (
                      <span className="badge bg-success">受取済</span>
                    ) : (
                      <span className="badge bg-warning">未受取</span>
                    )
                  ) : (
                    <span className="text-muted">なし</span>
                  )}
                </td>
                <td>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => setSelectedNotification(notification)}
                  >
                    詳細
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 詳細モーダル */}
      <Modal show={selectedNotification !== null} onHide={() => setSelectedNotification(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedNotification && <PersonalNotificationIcon icon={selectedNotification.icon} />}
            <span className="ms-2">{selectedNotification?.title}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedNotification && (
            <div>
              <div className="mb-3">
                <strong>コンテンツ:</strong>
                <div className="mt-2">
                  <QuillViewer content={selectedNotification.content} />
                </div>
              </div>

              {selectedNotification.delivery_content && (
                <div className="mb-3">
                  <strong>配信コンテンツ:</strong> あり
                </div>
              )}

              <div className="row mt-3 text-muted small">
                <div className="col-md-6">
                  <strong>配信日時:</strong> {formatDate(selectedNotification.scheduled_delivery_at)}
                </div>
                <div className="col-md-6">
                  <strong>更新日時:</strong> {formatDate(selectedNotification.updated_at)}
                </div>
                {selectedNotification.read_at && (
                  <div className="col-md-6 mt-2">
                    <strong>既読日時:</strong> {formatDate(selectedNotification.read_at.seconds * 1000)}
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedNotification(null)}>
            閉じる
          </Button>
          {selectedNotification && selectedNotification.delivery_content && !selectedNotification.delivery_content.received_at && (
            <Button 
              variant="primary"
              disabled={receivingContent === selectedNotification.id}
              onClick={() => handleReceiveContent(selectedNotification)}
            >
              {receivingContent === selectedNotification.id ? (
                <span>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  受け取り中
                </span>
              ) : (
                <span>
                  <i className="fas fa-gift me-1"></i>
                  受け取る
                </span>
              )}
            </Button>
          )}
          {selectedNotification && !selectedNotification.is_read && (
            <Button 
              variant="success"
              disabled={markingAsRead === selectedNotification.id}
              onClick={() => handleMarkAsRead(selectedNotification)}
            >
              {markingAsRead === selectedNotification.id ? (
                <span>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  処理中
                </span>
              ) : (
                "既読にする"
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  )
}
